import axiosInstance from './axiosConfig';
import { deriveProfileComplete } from '../utils/profileCompletion';

interface ClientAddress {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
}

interface Client {
  _id: string;
  client_id: string;
  name: string;
  email?: string;
  phone?: string;
  country_code?: string;
  status?: string;
  profile_picture?: string;
  has_password?: boolean;
  has_google?: boolean;
  rewards_points?: number;
  hasCompletedProfile?: boolean;
  address?: ClientAddress;
}

interface ProfileUpdateResult {
  client: Client;
  hasCompletedProfile: boolean;
}

interface SignUpData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  country?: string;
  state?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthResponse {
  token: string;
  client: Client;
  hasCompletedProfile?: boolean;
  isNewSocialUser?: boolean;
}

class AuthService {
  public token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('token');
  }

  async signUp(data: SignUpData): Promise<any> {
    console.log('🔴 [FRONTEND] AuthService.signUp called');
    console.log('🔴 [FRONTEND] SignUp data:', { ...data, password: '***' });
    console.log('🔴 [FRONTEND] API Base URL:', axiosInstance.defaults.baseURL);
    console.log('🔴 [FRONTEND] Full URL will be:', `${axiosInstance.defaults.baseURL}/api/client-auth/signup`);
    try {
      const response = await axiosInstance.post('/api/client-auth/signup', data);
      console.log('🔴 [FRONTEND] SignUp response received:', response.data);
      
      const { token, client, requiresVerification } = response.data;
      
      // Only set token if it's an immediate login (requiresVerification is false/undefined)
      if (token && client && !requiresVerification) {
        this.token = token;
        localStorage.setItem('token', token);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('AuthService: Sign up error:', error);
      const apiMessage = error.response?.data?.message;
      if (typeof apiMessage === 'string' && apiMessage.trim()) {
        throw new Error(apiMessage.trim());
      }
      throw error;
    }
  }

  async verifyOtp(email: string, otp: string): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<{ success: boolean; access_token: string; client: Client }>('/api/client-auth/verify-otp', {
        identifier: email,
        identifier_type: 'email',
        otp_code: otp,
      });
      const { access_token, client } = response.data;
      
      this.token = access_token;
      localStorage.setItem('token', access_token);

      const normalizedClient: Client = {
        ...client,
        hasCompletedProfile: deriveProfileComplete(client),
      };
      
      return {
        token: access_token,
        client: normalizedClient,
        hasCompletedProfile: normalizedClient.hasCompletedProfile,
      };
    } catch (error: any) {
      console.error('AuthService: Verify OTP error:', error);
      throw new Error(error.response?.data?.message || 'Verification failed');
    }
  }

  async resendOtp(email: string): Promise<boolean> {
    try {
      await axiosInstance.post('/api/client-auth/send-otp', {
        identifier: email,
        identifier_type: 'email',
      });
      return true;
    } catch (error: any) {
      console.error('AuthService: Resend OTP error:', error);
      throw new Error(error.response?.data?.message || 'Failed to resend OTP');
    }
  }

  async verifyMagicLink(token: string, email: string): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.get<{ success: boolean; token: string; client: Client }>(`/api/client-auth/verify-magic-link?token=${token}&email=${encodeURIComponent(email)}`);
      const { token: sessionToken, client } = response.data;
      
      this.token = sessionToken;
      localStorage.setItem('token', sessionToken);

      const normalizedClient: Client = {
        ...client,
        hasCompletedProfile: deriveProfileComplete(client),
      };
      
      return {
        token: sessionToken,
        client: normalizedClient,
        hasCompletedProfile: normalizedClient.hasCompletedProfile,
      };
    } catch (error: any) {
      console.error('AuthService: Verify Magic Link error:', error);
      throw new Error(error.response?.data?.message || 'Magic link verification failed');
    }
  }

  async login(credentials: LoginCredentials): Promise<any> {
    console.log('🔴 [FRONTEND] AuthService.login called');
    console.log('🔴 [FRONTEND] Login credentials:', { email: credentials.email, password: '***' });
    console.log('🔴 [FRONTEND] API Base URL:', axiosInstance.defaults.baseURL);
    console.log('🔴 [FRONTEND] Full URL will be:', `${axiosInstance.defaults.baseURL}/api/client-auth/login`);
    try {
      const response = await axiosInstance.post('/api/client-auth/login', credentials);
      console.log('🔴 [FRONTEND] Login response received:', response.data);
      const { token, client, requiresVerification } = response.data;

      if (requiresVerification) {
        return response.data;
      }
      
      if (token && client) {
        this.token = token;
        localStorage.setItem('token', token);
      }

      const normalizedClient = client
        ? { ...client, hasCompletedProfile: deriveProfileComplete(client) }
        : client;
      
      return { ...response.data, client: normalizedClient };
    } catch (error: any) {
      console.error('AuthService: Login error:', error);
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.token) {
        await axiosInstance.post('/api/client-auth/signout');
      }
    } catch (error) {
      console.error('AuthService: Logout error:', error);
    } finally {
      this.token = null;
      localStorage.removeItem('token');
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem('token');
    this.token = token;
    return token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async getCurrentClient(): Promise<Client> {
    try {
      const response = await axiosInstance.get<{ success: boolean; client: Client }>('/api/client-auth/me');
      const client = response.data.client;
      return {
        ...client,
        hasCompletedProfile: client.hasCompletedProfile ?? deriveProfileComplete(client),
      };
    } catch (error) {
      console.error('AuthService: Error fetching current client:', error);
      throw error;
    }
  }

  async signInWithGoogle(credential: string): Promise<AuthResponse> {
    console.log('🔴 [FRONTEND] AuthService.signInWithGoogle called');
    try {
      const response = await axiosInstance.post<{ success: boolean; token: string; client: Client; hasCompletedProfile?: boolean; isNewSocialUser?: boolean }>('/api/client-auth/google', {
        credential,
      });
      console.log('🔴 [FRONTEND] Google sign-in response received:', { success: response.data.success, hasToken: !!response.data.token, hasClient: !!response.data.client });
      const { token, client, hasCompletedProfile, isNewSocialUser } = response.data;
      
      this.token = token;
      localStorage.setItem('token', token);
      
      return { token, client, hasCompletedProfile, isNewSocialUser };
    } catch (error: any) {
      console.error('AuthService: Google sign-in error:', error);
      throw new Error(error.response?.data?.message || 'Google sign-in failed');
    }
  }

  async updateProfile(data: {
    name?: string;
    phone?: string;
    country_code?: string;
    address?: { country?: string; state_province?: string };
  }): Promise<ProfileUpdateResult> {
    try {
      const response = await axiosInstance.patch<{
        success: boolean;
        client: Client;
        hasCompletedProfile?: boolean;
      }>('/api/client-auth/profile', data);

      const client: Client = {
        ...response.data.client,
        hasCompletedProfile:
          response.data.hasCompletedProfile ?? response.data.client?.hasCompletedProfile,
      };

      return {
        client,
        hasCompletedProfile: deriveProfileComplete(client),
      };
    } catch (error: any) {
      console.error('AuthService: Update profile error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  async changePassword(currentPassword: string | null, newPassword: string): Promise<void> {
    try {
      const payload: { new_password: string; current_password?: string } = {
        new_password: newPassword,
      };
      if (currentPassword) {
        payload.current_password = currentPassword;
      }
      await axiosInstance.post('/api/client-auth/change-password', payload);
    } catch (error: any) {
      console.error('AuthService: Change password error:', error);
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  }

  async updateProfilePicture(profilePicture: string): Promise<Client> {
    try {
      const response = await axiosInstance.post<{ success: boolean; client: Client }>('/api/client-auth/profile-picture', {
        profile_picture: profilePicture,
      });
      return response.data.client;
    } catch (error: any) {
      console.error('AuthService: Update profile picture error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update profile picture');
    }
  }
}

export const authService = new AuthService();
export type { Client, ClientAddress, SignUpData, LoginCredentials, AuthResponse, ProfileUpdateResult };
